<script setup lang="ts">
import { computed, h, onMounted, ref, VNode } from 'vue';
import {
  NButton,
  NIcon,
  NPopconfirm,
  NSpace,
  type DataTableColumns,
  type FormInst,
  type FormRules,
  type SelectOption,
} from 'naive-ui';
import { IconAdd, IconDelete, IconEdit, IconRefresh } from '@/components/design/icons';
import Layout from '@/components/design/layout/Layout.vue';
import { navLabels } from '@/components/design/domain/navigation/navLabels';
import GaiaHeader from '@/components/design/base/GaiaHeader.vue';
import { gaiaContentWidth } from '@/components/design/themes/tokens';
import GaiaTable from '@/components/design/base/GaiaTable.vue';
import { useAdminModelPricingStore } from '@/stores/admin_model_pricing';
import type { LlmProvider, ModelPricingCreate, ModelPricingInDb } from '@/api';
import GaiaSelect from '@/components/design/base/selects/GaiaSelect.vue';
import GaiaDatePicker from '@/components/design/base/inputs/GaiaDatePicker.vue';
import GaiaInputNumber from '@/components/design/base/inputs/GaiaInputNumber.vue';
import { useGaiaToast } from '@/composables/useGaiaToast';

interface FormState {
  provider: LlmProvider | null;
  model: string | null;
  effective_from: number | null;
  effective_to: number | null;
  input_price_per_mtok: number | null;
  output_price_per_mtok: number | null;
  cached_input_price_per_mtok: number | null;
}

const store = useAdminModelPricingStore();
const message = useGaiaToast();

const showModal = ref<boolean>(false);
const isEditing = ref<boolean>(false);
const editingUid = ref<string>('');
const formRef = ref<FormInst | null>(null);

function emptyForm(): FormState {
  return {
    provider: null,
    model: null,
    effective_from: null,
    effective_to: null,
    input_price_per_mtok: null,
    output_price_per_mtok: null,
    cached_input_price_per_mtok: null,
  };
}

const formModel = ref<FormState>(emptyForm());

const providerOptions = computed<SelectOption[]>(() =>
  Object.keys(store.availableProviders).map(provider => ({
    label: provider,
    value: provider,
  }))
);

const modelOptions = computed<SelectOption[]>(() => {
  const provider = formModel.value.provider;
  if (!provider) return [];

  const spec = store.availableProviders[provider];
  if (!spec) return [];

  return Object.entries(spec.llm_models).map(([modelKey, modelSpec]) => ({
    label: `${modelSpec.display_name_model} (${modelKey})`,
    value: modelKey,
  }));
});

const formRules: FormRules = {
  provider: [{ required: true, message: 'Provider is required', trigger: ['blur', 'change'] }],
  model: [{ required: true, message: 'Model is required', trigger: ['blur', 'change'] }],
  effective_from: [
    {
      required: true,
      type: 'number',
      message: 'Effective From is required',
      trigger: ['blur', 'change'],
    },
  ],
  input_price_per_mtok: [
    {
      required: true,
      type: 'number',
      message: 'Input price is required',
      trigger: ['blur', 'change'],
    },
  ],
  output_price_per_mtok: [
    {
      required: true,
      type: 'number',
      message: 'Output price is required',
      trigger: ['blur', 'change'],
    },
  ],
};

function formatDate(value: string | null | undefined): string {
  if (!value) return '—';
  try {
    const date = new Date(value);
    return `${date.toLocaleDateString()} ${date.toLocaleTimeString()}`;
  } catch {
    return value;
  }
}

function formatPrice(value: string | null | undefined): string {
  if (value === null || value === undefined) return '—';
  const num = Number(value);
  if (Number.isNaN(num)) return value;
  return num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 6 });
}

const columns: DataTableColumns<ModelPricingInDb> = [
  { title: 'Provider', key: 'provider', sorter: 'default' },
  { title: 'Model', key: 'model', sorter: 'default' },
  {
    title: 'Effective From',
    key: 'effective_from',
    sorter: 'default',
    render: row => formatDate(row.effective_from),
  },
  {
    title: 'Effective To',
    key: 'effective_to',
    render: row => formatDate(row.effective_to),
  },
  {
    title: 'Input €/Mtok',
    key: 'input_price_per_mtok',
    render: row => formatPrice(row.input_price_per_mtok),
  },
  {
    title: 'Output €/Mtok',
    key: 'output_price_per_mtok',
    render: row => formatPrice(row.output_price_per_mtok),
  },
  {
    title: 'Cached Input €/Mtok',
    key: 'cached_input_price_per_mtok',
    render: row => formatPrice(row.cached_input_price_per_mtok),
  },
  {
    title: 'Updated At',
    key: 'updated_at',
    render: row => formatDate(row.updated_at),
  },
  {
    title: 'Actions',
    key: 'actions',
    fixed: 'right',
    width: 110,
    render(row): VNode {
      return h(
        NSpace,
        { align: 'center', justify: 'center', size: [8, 0], inline: true, wrap: false },
        {
          default: () => [
            h(
              NButton,
              {
                quaternary: true,
                size: 'small',
                onClick: () => {
                  openEditModal(row);
                },
              },
              { icon: () => h(NIcon, null, { default: () => h(IconEdit) }) }
            ),
            h(
              NPopconfirm,
              { onPositiveClick: () => deleteRow(row.uid) },
              {
                default: () => 'Delete this pricing row?',
                trigger: () =>
                  h(
                    NButton,
                    { quaternary: true, size: 'small', type: 'error' },
                    { icon: () => h(NIcon, null, { default: () => h(IconDelete) }) }
                  ),
              }
            ),
          ],
        }
      );
    },
  },
];

const knownProviderModelPair = computed<boolean>(() => {
  if (!isEditing.value) return true;
  const provider = formModel.value.provider;
  const model = formModel.value.model;
  if (!provider || !model) return true;
  const spec = store.availableProviders[provider];
  if (!spec) return false;
  return Object.prototype.hasOwnProperty.call(spec.llm_models, model);
});

function openCreateModal(): void {
  isEditing.value = false;
  editingUid.value = '';
  formModel.value = emptyForm();
  showModal.value = true;
}

function openEditModal(row: ModelPricingInDb): void {
  isEditing.value = true;
  editingUid.value = row.uid;

  formModel.value = {
    provider: row.provider,
    model: row.model,
    effective_from: new Date(row.effective_from).getTime(),
    effective_to: row.effective_to ? new Date(row.effective_to).getTime() : null,
    input_price_per_mtok: Number(row.input_price_per_mtok),
    output_price_per_mtok: Number(row.output_price_per_mtok),
    cached_input_price_per_mtok:
      row.cached_input_price_per_mtok !== null && row.cached_input_price_per_mtok !== undefined
        ? Number(row.cached_input_price_per_mtok)
        : null,
  };
  showModal.value = true;
}

function handleProviderChange(): void {
  // Reset model when provider changes during create flow.
  if (!isEditing.value) {
    formModel.value.model = null;
  }
}

function timestampToIsoString(timestamp: number | null): string | null {
  if (timestamp === null) return null;
  return new Date(timestamp).toISOString();
}

function submitForm(): void {
  void formRef.value?.validate(errors => {
    void (async (): Promise<void> => {
      if (errors) return;

      const effectiveFromIso = timestampToIsoString(formModel.value.effective_from);
      if (!effectiveFromIso) return;

      if (isEditing.value) {
        const result = await store.updateRow(editingUid.value, {
          effective_from: effectiveFromIso,
          effective_to: timestampToIsoString(formModel.value.effective_to),
          input_price_per_mtok: formModel.value.input_price_per_mtok ?? undefined,
          output_price_per_mtok: formModel.value.output_price_per_mtok ?? undefined,
          cached_input_price_per_mtok: formModel.value.cached_input_price_per_mtok,
        });
        if (result) {
          message.success('Pricing row updated');
          showModal.value = false;
        } else if (store.error) {
          message.error(store.error);
        }
        return;
      }

      if (!formModel.value.provider || !formModel.value.model) return;

      const body: ModelPricingCreate = {
        provider: formModel.value.provider,
        model: formModel.value.model,
        effective_from: effectiveFromIso,
        effective_to: timestampToIsoString(formModel.value.effective_to),
        input_price_per_mtok: formModel.value.input_price_per_mtok ?? 0,
        output_price_per_mtok: formModel.value.output_price_per_mtok ?? 0,
        cached_input_price_per_mtok: formModel.value.cached_input_price_per_mtok,
      };

      const result = await store.createRow(body);
      if (result) {
        message.success('Pricing row created');
        showModal.value = false;
      } else if (store.error) {
        message.error(store.error);
      }
    })();
  });
}

async function deleteRow(uid: string): Promise<void> {
  const success = await store.deleteRow(uid);
  if (success) {
    message.success('Pricing row deleted');
  } else if (store.error) {
    message.error(store.error);
  }
}

onMounted(async () => {
  await Promise.all([store.fetchAvailableProviders(), store.fetchRows()]);
});
</script>

<template>
  <Layout :content-max-width="gaiaContentWidth.table">
    <template #breadcrumb>
      <n-breadcrumb-item>{{ navLabels.adminArea }}</n-breadcrumb-item>
      <n-breadcrumb-item>{{ navLabels.modelCosts }}</n-breadcrumb-item>
    </template>

    <n-flex vertical :size="18" :wrap="false">
      <gaia-header :level="1" variant="display" size="screen" :style="{ margin: 0 }">
        {{ navLabels.modelCosts }}
      </gaia-header>

      <n-space justify="space-between" align="center" style="margin-bottom: 16px">
        <n-text depth="3">
          Pricing rows define the per-million-token cost of each (provider, model) over time.
        </n-text>
        <n-space>
          <n-button @click="store.fetchRows()">
            <template #icon>
              <n-icon><IconRefresh /></n-icon>
            </template>
            Refresh
          </n-button>
          <n-button type="primary" @click="openCreateModal">
            <template #icon>
              <n-icon><IconAdd /></n-icon>
            </template>
            New Pricing Row
          </n-button>
        </n-space>
      </n-space>
    </n-flex>

    <n-alert v-if="store.error" type="error" :title="store.error" style="margin-bottom: 16px" />

    <gaia-table
      :columns="columns"
      :data="store.rows"
      :loading="store.loading"
      :pagination="false"
      striped
    />

    <n-empty
      v-if="!store.loading && !store.hasRows"
      description="No pricing rows yet"
      style="margin-top: 24px"
    />

    <n-modal
      v-model:show="showModal"
      preset="card"
      :title="isEditing ? 'Edit Pricing Row' : 'New Pricing Row'"
      style="width: 540px"
      :mask-closable="false"
    >
      <n-form
        ref="formRef"
        :model="formModel"
        :rules="formRules"
        label-placement="left"
        label-width="160"
        require-mark-placement="right-hanging"
      >
        <n-form-item label="Provider" path="provider">
          <gaia-select
            v-model:value="formModel.provider"
            :options="providerOptions"
            :disabled="isEditing"
            placeholder="Select provider"
            @update:value="handleProviderChange"
          />
        </n-form-item>

        <n-form-item label="Model" path="model">
          <gaia-select
            v-model:value="formModel.model"
            :options="modelOptions"
            :disabled="isEditing || !formModel.provider"
            placeholder="Select model"
            filterable
          />
        </n-form-item>

        <n-form-item label="Effective From" path="effective_from">
          <gaia-date-picker
            v-model:value="formModel.effective_from"
            type="datetime"
            clearable
            style="width: 100%"
          />
        </n-form-item>

        <n-form-item label="Effective To">
          <gaia-date-picker
            v-model:value="formModel.effective_to"
            type="datetime"
            clearable
            style="width: 100%"
          />
        </n-form-item>

        <n-form-item label="Input €/Mtok" path="input_price_per_mtok">
          <gaia-input-number
            v-model:value="formModel.input_price_per_mtok"
            :min="0"
            :precision="6"
            placeholder="0.00"
            style="width: 100%"
          />
        </n-form-item>

        <n-form-item label="Output €/Mtok" path="output_price_per_mtok">
          <gaia-input-number
            v-model:value="formModel.output_price_per_mtok"
            :min="0"
            :precision="6"
            placeholder="0.00"
            style="width: 100%"
          />
        </n-form-item>

        <n-form-item label="Cached Input €/Mtok">
          <gaia-input-number
            v-model:value="formModel.cached_input_price_per_mtok"
            :min="0"
            :precision="6"
            placeholder="Optional"
            clearable
            style="width: 100%"
          />
        </n-form-item>

        <n-alert v-if="!knownProviderModelPair" type="warning" style="margin-top: 8px">
          This row references a provider/model the backend does not currently report as available.
        </n-alert>
      </n-form>

      <template #footer>
        <n-space justify="end">
          <n-button @click="showModal = false">Cancel</n-button>
          <n-button type="primary" :loading="store.loading" @click="submitForm">
            {{ isEditing ? 'Save Changes' : 'Create' }}
          </n-button>
        </n-space>
      </template>
    </n-modal>
  </Layout>
</template>
